/**
 * SunoMidiViewerSheet — показ MIDI-результата (список инструментов +
 * кнопка «Скачать .mid»). Аналогично CoverPickerSheet — смонтирован на App
 * уровне, открывается при pendingViewer в SunoMidiJobsContext.
 */
import Sheet from '../../../components/Sheet'
import { Icon } from '../../../../public/icons/Icon.tsx'
import { showToast } from '../../../components/Toast'
import { useSunoMidiJobs } from '../../../context/SunoMidiJobsContext'
import {
	parseMidiResponseData,
	buildMidiFile,
	downloadMidi,
} from '../utils/sunoMidi'

function pluralRu(n: number, one: string, few: string, many: string): string {
	const mod10 = n % 10
	const mod100 = n % 100
	if (mod10 === 1 && mod100 !== 11) return one
	if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few
	return many
}

export default function SunoMidiViewerSheet() {
	const { pendingViewer, dismissViewer } = useSunoMidiJobs()
	if (!pendingViewer) return null

	const instruments = parseMidiResponseData(pendingViewer.midiData) ?? []
	const files = pendingViewer.files ?? []
	const totalNotes = instruments.reduce((s, i) => s + (i.notes?.length ?? 0), 0)

	const handleDownload = () => {
		try {
			const bytes = buildMidiFile(instruments)
			if (!bytes) {
				showToast('Нет валидных нот для экспорта', 'error')
				return
			}
			const safe = (pendingViewer.title?.trim() || 'track')
				.replace(/[\\/:"*?<>|]/g, '_')
				.slice(0, 100)
			// Метка времени в имени — чтобы повторные выгрузки не сливались в один
			// файл. Вызов по КЛИКУ, не в рендере: чистоту рендера не нарушает,
			// но по телу обработчика линтер этого не видит.
			// eslint-disable-next-line react-hooks/purity
			downloadMidi(bytes, `${safe}-${Date.now()}.mid`)
		} catch (err) {
			showToast(err instanceof Error ? err.message : 'Ошибка конвертации в .mid', 'error')
		}
	}

	return (
		<Sheet
			open={!!pendingViewer}
			onClose={dismissViewer}
			variant="center"
			title="MIDI трека"
			className="suno-midi-viewer"
		>
			<div className="suno-midi-viewer__body">
				<div className="suno-midi-viewer__track">
					{pendingViewer.title?.trim() || 'Без названия'}
				</div>

				<p className="suno-midi-viewer__hint">
					MIDI — это не аудио, а ноты. Открой .mid в DAW (Logic / Ableton /
					FL Studio / Reaper) — сможешь заменить инструменты, поправить ноты,
					сделать аранжировку.
				</p>

				{instruments.length === 0 && files.length > 0 ? (
					/* Mureka отдаёт ноты АРХИВОМ .mid-файлов, а не JSON'ом: рисовать
					   нечего, но файлы — это и есть результат. Раньше до них
					   добирались только через треки-дубли в библиотеке. */
					<div className="suno-midi-viewer__files">
						{files.map((f) => (
							<a
								key={f.url}
								className="suno-midi-viewer__file"
								href={f.url}
								download={f.name}
							>
								<span className="suno-midi-viewer__file-name">{f.name}</span>
								<span className="suno-midi-viewer__file-hint">скачать</span>
							</a>
						))}
					</div>
				) : instruments.length === 0 ? (
					<div className="suno-midi-viewer__empty">
						Нот не найдено в ответе. Возможно MIDI ещё не готов полностью.
					</div>
				) : (
					<>
						<div className="suno-midi-viewer__summary">
							<span>
								{instruments.length} инструмент
								{pluralRu(instruments.length, '', 'а', 'ов')} ·{' '}
								{totalNotes} нот{pluralRu(totalNotes, 'а', 'ы', '')}
							</span>
							<button
								type="button"
								className="suno-midi-viewer__download"
								onClick={handleDownload}
								disabled={totalNotes === 0}
								title="Скачать как стандартный MIDI-файл"
							>
								<Icon id="gen-download" size={12} />
								Скачать .mid
							</button>
						</div>
						<ul className="suno-midi-viewer__list">
							{instruments.map((inst, i) => (
								<li key={i} className="suno-midi-viewer__item">
									<span className="suno-midi-viewer__item-name">
										{inst.is_drum ? '🥁 ' : ''}
										{inst.name || `Инструмент ${i + 1}`}
									</span>
									<span className="suno-midi-viewer__item-count">
										{inst.notes?.length ?? 0} нот
									</span>
								</li>
							))}
						</ul>
					</>
				)}

			</div>
		</Sheet>
	)
}
